import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, Eye, EyeOff, Loader2, ShieldCheck } from 'lucide-react';
import GoogleTranslate from '../components/GoogleTranslate';
import '../styles/App.css';

import BrandLogo from '../components/common/BrandLogo';
function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({ email: false, password: false });
  const [requires2Fa, setRequires2Fa] = useState(false);
  const [twoFaUserId, setTwoFaUserId] = useState(null);
  const [twoFaCode, setTwoFaCode] = useState('');
  const { login, loginWith2Fa } = useAuth();
  const navigate = useNavigate();

  const validateEmail = (email) => {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
  };

  const getEmailError = () => {
    if (!touched.email) return '';
    if (!email) return 'Email is required';
    if (!validateEmail(email)) return 'Please enter a valid email address';
    return '';
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ email: true, password: true });

    if (!validateEmail(email) || !password) {
      setError('Please fix the errors before logging in.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result.success) {
        if (result.require2Fa) {
          setRequires2Fa(true);
          setTwoFaUserId(result.userId);
        } else {
          navigate('/dashboard');
        }
      } else {
        setError(result.error || 'Invalid credentials. Please try again.');
      }
    } catch (err) {
      setError('A connection error occurred. Please check your network.');
    } finally {
      setLoading(false);
    }
  };

  const handle2FaSubmit = async (e) => {
    e.preventDefault();
    if (twoFaCode.length < 6) return setError('Enter a 6-digit code');
    setError('');
    setLoading(true);
    try {
      const result = await loginWith2Fa(twoFaUserId, twoFaCode);
      if (result.success) {
        navigate('/dashboard');
      } else {
        setError(result.error || 'Invalid code.');
      }
    } catch (err) {
      setError('Connection error.');
    } finally {
      setLoading(false);
    }
  };

  const emailErr = getEmailError();

  return (
    <div className="auth-split-wrapper">
      {/* Branding Side */}
      <div className="auth-brand-side">
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          backgroundImage: 'url("/auth-bg-login.png")',
          backgroundSize: 'cover', backgroundPosition: 'center',
          animation: 'slowZoom 30s infinite alternate ease-in-out',
          zIndex: 1
        }} />
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          background: 'linear-gradient(to right, rgba(0,0,0,0.6), rgba(0,0,0,0.3))',
          zIndex: 2
        }} />
        
        <div style={{ position: 'relative', zIndex: 3, maxWidth: '600px', textAlign: 'left' }}>
          <div style={{ marginBottom: '2rem' }}>
            <BrandLogo style={{ fontSize: '2rem' }} />
          </div>
          <h1 style={{ 
            fontSize: '3.5rem', fontWeight: '900', color: 'white', 
            lineHeight: '1.1', marginBottom: '1.5rem', letterSpacing: '-0.02em'
          }}>
            Securing the Future of <span style={{ color: '#FCD535' }}>Quantitative Wealth.</span>
          </h1>
          <p style={{ 
            fontSize: '1.1rem', color: 'rgba(255,255,255,0.7)', 
            maxWidth: '500px', lineHeight: '1.6', fontWeight: '400' 
          }}>
            Quantis Capital merges elite algorithmic infrastructure with uncompromising digital asset security to deliver unparalleled institutional performance.
          </p>
          
          <div style={{ marginTop: '3rem', display: 'flex', gap: '2rem' }}>
            <div>
              <div style={{ fontSize: '1.5rem', fontWeight: '800', color: 'white' }}>$2.4B+</div>
              <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '1px' }}>AUM Managed</div>
            </div>
            <div style={{ width: '1px', background: 'rgba(255,255,255,0.1)' }} />
            <div>
              <div style={{ fontSize: '1.5rem', fontWeight: '800', color: 'white' }}>120K+</div>
              <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '1px' }}>Global Clients</div>
            </div>
          </div>
        </div>
      </div>

      {/* Form Side */}
      <div className="auth-form-side">
        <div className="auth-card glass" style={{ 
          maxWidth: '440px', width: '100%', padding: '3rem', 
          borderRadius: '32px', border: '1px solid var(--border-color)',
          background: 'var(--bg-modal)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.1)'
        }}>
          {requires2Fa ? (
            <div style={{ animation: 'fadeInUp 0.4s ease-out' }}>
              <div className="logo" style={{ marginBottom: '1.5rem', textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
                <div style={{ 
                  background: 'rgba(252, 213, 53, 0.1)', 
                  padding: '1.5rem', borderRadius: '24px',
                  border: '1px solid rgba(252, 213, 53, 0.2)'
                }}>
                  <ShieldCheck size={48} color="#FCD535" />
                </div>
              </div>
              <h2 style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', textAlign: 'center', fontSize: '1.75rem', fontWeight: '800' }}>Verification</h2>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem', textAlign: 'center', fontSize: '0.9rem' }}>Enter the 6-digit security code from your device.</p>

              {error && requires2Fa && (
                <div style={{
                  backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', padding: '1rem',
                  borderRadius: '16px', marginBottom: '2rem', fontSize: '0.85rem',
                  border: '1px solid rgba(239, 68, 68, 0.2)', display: 'flex', alignItems: 'center', gap: '0.75rem'
                }}>
                  <Lock size={16} /> {error}
                </div>
              )}

              <form onSubmit={handle2FaSubmit}>
                <div className="form-group" style={{ marginBottom: '2.5rem' }}>
                  <input
                    type="text"
                    maxLength={6}
                    value={twoFaCode}
                    onChange={e => setTwoFaCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    style={{
                      width: '100%', padding: '1.25rem', borderRadius: '16px', border: '1.5px solid var(--border-color)',
                      background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '2rem',
                      textAlign: 'center', letterSpacing: '8px', fontWeight: '800', transition: 'all 0.3s', outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#FCD535'}
                    onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
                    disabled={loading}
                  />
                </div>
                <button type="submit" className="btn btn-primary" style={{ 
                  width: '100%', padding: '1.1rem', borderRadius: '16px', fontSize: '1rem', 
                  fontWeight: '700', display: 'flex', justifyContent: 'center', alignItems: 'center', 
                  gap: '0.75rem', marginBottom: '1rem',
                  background: 'linear-gradient(135deg, #FCD535 0%, #d97706 100%)',
                  color: '#000', border: 'none', boxShadow: '0 10px 20px rgba(245, 158, 11, 0.2)'
                }} disabled={loading}>
                  {loading ? <Loader2 size={18} className="spinner" /> : 'Authorize Access'}
                </button>
                <button type="button" onClick={() => { setRequires2Fa(false); setTwoFaCode(''); }} style={{ width: '100%', padding: '0.8rem', background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: '0.9rem', cursor: 'pointer', fontWeight: '600' }}>
                  Back to Security Portal
                </button>
              </form>
            </div>
          ) : (
            <div style={{ animation: 'fadeInUp 0.4s ease-out' }}>
              <div style={{ display: 'none' }}>{/* Hidden on large, but visible for mobile brand identification if needed */}
                <BrandLogo style={{ marginBottom: '2rem', justifyContent: 'center' }} />
              </div>

              <div style={{ marginBottom: '2.5rem' }}>
                <h2 style={{ color: 'var(--text-primary)', fontSize: '2.2rem', fontWeight: '900', letterSpacing: '-0.02em', marginBottom: '0.5rem' }}>Login</h2>
                <p style={{ color: 'var(--text-secondary)', fontSize: '1rem' }}>Enter your credentials to access your portal.</p>
              </div>

              {error && !requires2Fa && (
                <div style={{
                  backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', padding: '1rem',
                  borderRadius: '16px', marginBottom: '2rem', fontSize: '0.85rem',
                  border: '1px solid rgba(239, 68, 68, 0.2)', display: 'flex', alignItems: 'center', gap: '0.75rem'
                }}>
                  <Lock size={16} /> {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.75rem', fontSize: '0.85rem', fontWeight: '600', color: 'rgba(255,255,255,0.6)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Email Address</label>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '1.25rem', top: '50%', transform: 'translateY(-50%)', color: emailErr ? '#ef4444' : 'rgba(255,255,255,0.3)' }}>
                      <Mail size={18} />
                    </span>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onBlur={() => handleBlur('email')}
                      placeholder="institution@quantis.com"
                      style={{
                        width: '100%',
                        padding: '1.1rem 1rem 1.1rem 3.2rem',
                        borderRadius: '16px',
                        border: `1.5px solid ${emailErr ? '#ef4444' : 'var(--border-color)'}`,
                        background: 'var(--bg-secondary)',
                        color: 'var(--text-primary)',
                        fontSize: '1rem',
                        transition: 'all 0.3s ease',
                        outline: 'none'
                      }}
                      onFocus={(e) => !emailErr && (e.target.style.borderColor = '#FCD535')}
                      onBlur={(e) => !emailErr && (e.target.style.borderColor = 'rgba(255,255,255,0.1)')}
                      disabled={loading}
                    />
                  </div>
                  {emailErr && <p style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '0.5rem', fontWeight: '500' }}>{emailErr}</p>}
                </div>

                <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    <label style={{ fontSize: '0.85rem', fontWeight: '600', color: 'rgba(255,255,255,0.6)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Password</label>
                    <button type="button" onClick={() => navigate('/forgot-password')} style={{ background: 'none', border: 'none', padding: 0, color: '#FCD535', fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer' }}>
                      Recover Key
                    </button>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '1.25rem', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.3)' }}>
                      <Lock size={18} />
                    </span>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onBlur={() => handleBlur('password')}
                      placeholder="••••••••"
                      style={{
                        width: '100%',
                        padding: '1.1rem 3.5rem 1.1rem 3.2rem',
                        borderRadius: '16px',
                        border: '1.5px solid var(--border-color)',
                        background: 'var(--bg-secondary)',
                        color: 'var(--text-primary)',
                        fontSize: '1rem',
                        transition: 'all 0.3s ease',
                        outline: 'none'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#FCD535'}
                      onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: 'absolute',
                        right: '1rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        color: 'rgba(255,255,255,0.3)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '2.5rem' }}>
                  <input type="checkbox" id="remember" style={{ 
                    cursor: 'pointer', width: '18px', height: '18px', 
                    accentColor: '#FCD535', border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '4px', background: 'transparent'
                  }} />
                  <label htmlFor="remember" style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', userSelect: 'none' }}>Remember this device</label>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{
                    width: '100%',
                    padding: '1.25rem',
                    borderRadius: '16px',
                    fontSize: '1.1rem',
                    fontWeight: '800',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.75rem',
                    background: 'linear-gradient(135deg, #FCD535 0%, #d97706 100%)',
                    color: '#000',
                    border: 'none',
                    boxShadow: '0 15px 30px rgba(245, 158, 11, 0.25)',
                    transition: 'all 0.3s ease'
                  }}
                  disabled={loading}
                >
                  {loading ? <Loader2 size={22} className="spinner" /> : 'Enter Secure Portal'}
                </button>
              </form>

              <div style={{ marginTop: '2.5rem', textAlign: 'center', fontSize: '1rem', color: 'rgba(255,255,255,0.4)' }}>
                Don't have an account? <Link to="/register" style={{ color: '#FCD535', fontWeight: '800', textDecoration: 'none' }}>Register Now</Link>
              </div>
              <div style={{ marginTop: '2rem', opacity: 0.7 }}>
                <GoogleTranslate />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Login;
